import React, { useEffect, useMemo, useState } from "react";
import { api, API, authedFetch } from "../lib/api";
import { ImageIcon, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { openLightbox } from "../components/Lightbox";
import { formatDate } from "../components/dpi";
import { useAuth } from "../context/AuthContext";

const NOTE1_LABELS = {
  tabella_portata_errata: "Tabella portata errata",
  tabella_portata_mancante: "Tabella portata mancante",
  deformazione: "Deformazione",
  modifica_non_conforme: "Modifica non conforme",
  torsione: "Torsione",
  fissaggio_mancante: "Fissaggio mancante",
  pezzo_mancante: "Pezzo mancante",
};

function GalleryThumb({ item }) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let currentUrl = null;
    (async () => {
      try {
        const res = await authedFetch(`${API}/files/${item.photo}`);
        if (!res.ok) return;
        const b = await res.blob();
        const u = URL.createObjectURL(b);
        if (cancelled) {
          URL.revokeObjectURL(u);
        } else {
          currentUrl = u;
          setUrl(u);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
      if (currentUrl) {
        try { URL.revokeObjectURL(currentUrl); } catch { /* ignore */ }
      }
    };
  }, [item.photo]);

  const caption = `${item.scaffold_reference} · ${formatDate(item.revision_date)}${item.posto ? ` · ${item.posto}` : ""}`;
  const note = item.nota1 ? NOTE1_LABELS[item.nota1] : item.nota2 || "";

  return (
    <button
      type="button"
      onClick={() => openLightbox(item.photo, `${caption}${note ? " · " + note : ""}`)}
      className="bru-card overflow-hidden group text-left"
      data-testid={`gallery-thumb-${item.photo}`}
    >
      <div className="relative aspect-square bg-neutral-100">
        {url ? (
          <img src={url} alt={caption} className="w-full h-full object-cover cursor-zoom-in transition-transform group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-neutral-500" />
          </div>
        )}
      </div>
      <div className="px-3 py-2 border-t-[1.5px] border-black">
        <div className="flex justify-between text-[11px]">
          <span className="font-mono truncate mr-2">{item.posto || `#${item.row_number}`}</span>
          <span className="shrink-0">{item.riferimento_scaffalatura || "—"}</span>
        </div>
        {note && <div className="text-[11px] text-[#ff5a00] mt-1 truncate">{note}</div>}
      </div>
    </button>
  );
}

function RevisionGroup({ revisionDate, items, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 bg-[#f5f5f5] border-[1.5px] border-black rounded-md hover:bg-[#fff7f0]"
        data-testid={`gallery-revision-toggle-${revisionDate}`}
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <div className="tag-label">VERIFICA</div>
          <span className="font-bold">{formatDate(revisionDate)}</span>
        </div>
        <span className="text-xs font-bold">{items.length} foto</span>
      </button>
      {open && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mt-3">
          {items.map((it, idx) => (
            <GalleryThumb key={`${it.photo}-${idx}`} item={it} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReferenceGroup({ refName, items, address, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const byRevision = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const k = it.revision_date || "—";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(it);
    }
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [items]);
  return (
    <div className="bru-card p-4 mb-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid={`gallery-reference-toggle-${refName}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          <div>
            <div className="tag-label">SCAFFALATURA</div>
            <div className="text-xl font-bold truncate" >{refName}</div>
            {address && <div className="text-xs text-neutral-500 truncate">{address}</div>}
          </div>
        </div>
        <span className="bru-badge bg-white shrink-0">{items.length} foto</span>
      </button>
      {open && (
        <div className="mt-4">
          {byRevision.map(([date, rows], idx) => (
            <RevisionGroup key={date} revisionDate={date} items={rows} defaultOpen={idx === 0} />
          ))}
        </div>
      )}
    </div>
  );
}

function ClientGroup({ clientName, items, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const byRef = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const k = it.scaffold_reference || "—";
      if (!map.has(k)) map.set(k, { items: [], address: it.address });
      map.get(k).items.push(it);
    }
    return Array.from(map.entries()).sort((a, b) => (a[0] || "").localeCompare(b[0] || ""));
  }, [items]);
  return (
    <div className="mb-6">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-black text-white border-[1.5px] border-black rounded-md hover:bg-[#1a1a1a]"
        data-testid={`gallery-client-toggle-${clientName}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {open ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          <div className="text-left min-w-0">
            <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-300">CLIENTE</div>
            <div className="text-lg font-bold truncate" >{clientName}</div>
          </div>
        </div>
        <span className="bg-[#ff5a00] text-black px-2 py-0.5 text-xs font-bold rounded shrink-0">{items.length} foto</span>
      </button>
      {open && (
        <div className="mt-4 pl-0 sm:pl-4">
          {byRef.map(([ref, { items: rows, address }], idx) => (
            <ReferenceGroup key={ref} refName={ref} items={rows} address={address} defaultOpen={idx === 0} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Gallery() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/scaffolds/gallery");
        setItems(data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(
    () =>
      items.filter((it) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          (it.scaffold_reference || "").toLowerCase().includes(q) ||
          (it.client_name || "").toLowerCase().includes(q) ||
          (it.posto || "").toLowerCase().includes(q) ||
          (it.address || "").toLowerCase().includes(q) ||
          (it.riferimento_scaffalatura || "").toLowerCase().includes(q)
        );
      }),
    [items, search]
  );

  // Group by client (admin) or directly by reference (client role)
  const byClient = useMemo(() => {
    const map = new Map();
    for (const it of filtered) {
      const k = it.client_name || "—";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(it);
    }
    return Array.from(map.entries()).sort((a, b) => (a[0] || "").localeCompare(b[0] || ""));
  }, [filtered]);

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="gallery-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <h1 className="text-5xl lg:text-6xl font-bold tracking-tight leading-none" >
            Galleria <span className="text-[#ff5a00]">danni</span>
          </h1>
        </div>
        <input
          placeholder="Cerca scaffalatura, cliente, posto…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bru-input w-full sm:w-72"
          data-testid="gallery-search-input"
        />
      </div>

      {loading ? (
        <div className="bru-card p-12 text-center"><div className="tag-label">CARICAMENTO…</div></div>
      ) : filtered.length === 0 ? (
        <div className="bru-card p-12 text-center" data-testid="gallery-empty">
          <ImageIcon className="w-10 h-10 mx-auto mb-3 text-neutral-400" />
          <div className="tag-label mb-2">VUOTO</div>
          <h3 className="text-xl font-bold">
            {items.length === 0 ? "Nessuna foto di danno disponibile" : "Nessun risultato"}
          </h3>
        </div>
      ) : isAdmin ? (
        <div data-testid="gallery-grouped-admin">
          {byClient.map(([client, rows], idx) => (
            <ClientGroup key={client} clientName={client} items={rows} defaultOpen={idx === 0} />
          ))}
        </div>
      ) : (
        <div data-testid="gallery-grouped-client">
          {(() => {
            const map = new Map();
            for (const it of filtered) {
              const k = it.scaffold_reference || "—";
              if (!map.has(k)) map.set(k, { items: [], address: it.address });
              map.get(k).items.push(it);
            }
            const arr = Array.from(map.entries()).sort((a, b) => (a[0] || "").localeCompare(b[0] || ""));
            return arr.map(([ref, { items: rows, address }], idx) => (
              <ReferenceGroup key={ref} refName={ref} items={rows} address={address} defaultOpen={idx === 0} />
            ));
          })()}
        </div>
      )}
    </div>
  );
}
